import React, { useMemo, useState } from 'react';

import { Divider, IconButton, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import ButtonsGrid from 'components/_dialogs/_components/ButtonsGrid';
import AssetContactDialog from 'components/_dialogs/AssetContactDialog/AssetContactDialog';
import UploadFileDialog from 'components/_dialogs/UploadFileDialog/UploadFileDialog';
import AssetRelationsTable from 'components/AssetRelationsTable/AssetRelationsTable';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import DetailsCardHeader from 'components/DetailsCardHeader/DetailsCardHeader';
import FormAutocomplete from 'components/FormAutocomplete';
import FormCheckboxTile from 'components/FormCheckboxTile';
import FormSelect from 'components/FormSelect';
import FormTextInput from 'components/FormTextInput';
import InputInfoWrapper from 'components/InputInfoWrapper/InputInfoWrapper';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import assetCategoriesApi from 'config/api/assetCategories/assetCategories';
import assetContactsApi from 'config/api/assetContacts/assetContacts';
import assetDocumentsApi from 'config/api/assetDocuments/assetDocuments';
import { AssetDocument } from 'config/api/assetDocuments/assetDocuments.types';
import assetInformationChoicesApi from 'config/api/assetInformationChoices/assetInformationChoices';
import { assetsApi } from 'config/api/assets/assets';
import { AssetDetails, AssetInfo } from 'config/api/assets/assets.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import DATE_FORMAT from 'config/constants/DATE_FORMAT';
import ROLES from 'config/constants/ROLES';
import {
  buildCustomerUploadFileValidator,
  CUSTOMER_UPLOAD_ACCEPTED_TYPES,
} from 'helpers/customerUploadValidation/customerUploadValidation';
import downloadFromUrl from 'helpers/downloadFromUrl/downloadFromUrl';
import safeT from 'helpers/safeT/safeT';
import stringShortener from 'helpers/stringShortener';
import useBoolState from 'hooks/useBoolState';
import usePermissions from 'hooks/usePermissions';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import coworkerOptionsParser from 'services/autocompleteServices/coworkerOptionsParser/coworkerOptionsParser';
import coworkerParamsGetter from 'services/autocompleteServices/coworkerParamsGetter/coworkerParamsGetter';
import coworkerResultComponent from 'services/autocompleteServices/coworkerResultComponent/coworkerResultComponent';
import { Id } from 'types/Id';

import { FileInForm } from '../../types/FileForm';

import useStyles from './AssetEditForm.styles';

type Props = {
  initialData: AssetDetails;
  id: Id;
};

export type EditAssetFormInput = {
  name: string;
  categoryId: Id;
  owner: { key: Id; fullName: string; email?: string }[];
  formalOwner: { key: Id; fullName: string; email?: string }[];
  info: AssetInfo & {
    legalDemandsCheckbox: boolean;
    identifiableInformationCheckbox: boolean;
    sensitiveIndividualInformationCheckbox: boolean;
  };
};

const FORM_ID = 'AssetEditForm';

const AssetEditForm: React.FC<Props> = ({ id, initialData }) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const validateCustomerUploadFile = useMemo(() => buildCustomerUploadFileValidator(t), [t]);
  // @ts-ignore
  const { showConfirmationModal } = useConfirmationModalContext();
  const { state: isUploadDialogOpen, setTrue: openUploadDialog, setFalse: closeUploadDialog } = useBoolState(false);
  const [contactDialog, setContactDialog] = useState<{ isOpen: boolean; contactId: Id | null }>({
    contactId: null,
    isOpen: false,
  });

  const [isAssetManager] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);

  const onClose = () => history.goBack();
  const closeContactDialog = () => setContactDialog({ contactId: null, isOpen: false });
  const openContactDialog = (contactId: Id | null) => () => setContactDialog({ contactId, isOpen: true });

  const mutation = useMutation(QUERY_KEYS.ASSETS_UPDATE, assetsApi.updateAsset, {
    onSuccess: () => {
      enqueueSnackbar(t(general_messages.data_saved), { variant: 'success' });
      onClose();
    },
  });

  const onSubmit = (formData: EditAssetFormInput) => {
    mutation.mutate({ id, data: formData });
  };

  const { data: categoriesOptions } = useQuery(QUERY_KEYS.ASSETS_CATEGORIES, assetCategoriesApi.getAllAssetCategoriesForSelect);
  const { data: assetsInformationChoices } = useQuery(
    QUERY_KEYS.ASSETS_INFORMATION_CHOICES,
    assetInformationChoicesApi.getAssetInformationChoices,
  );

  const onRemoveContact = (contactId: Id) => async () => {
    const confirmed = await showConfirmationModal({
      title: t(general_messages.are_you_sure),
      body: t(general_messages.operation_irreversible),
    });
    if (confirmed) {
      await assetContactsApi.removeAssetContact(contactId);
      await queryClient.invalidateQueries([QUERY_KEYS.ASSETS_GET_SINGLE, id.toString()]);
    }
  };

  const onDocumentRemove = async (documentData: AssetDocument) => {
    const confirmed = await showConfirmationModal({
      title: t(general_messages.are_you_sure),
      body: t(general_messages.operation_irreversible),
    });
    if (confirmed) {
      await assetDocumentsApi.removeAssetDocument(documentData.id);
      await queryClient.invalidateQueries([QUERY_KEYS.ASSETS_GET_SINGLE, id.toString()]);
    }
  };

  const onFileDialogSubmit = async (formData: FileInForm) => {
    await assetDocumentsApi.addAssetDocument({ file: formData.file, label: formData.name, asset: id });
    await queryClient.invalidateQueries([QUERY_KEYS.ASSETS_GET_SINGLE, id.toString()]);
  };

  const formik = useFormik<EditAssetFormInput>({
    onSubmit,
    initialValues: {
      name: initialData.name || '',
      categoryId: initialData.category.id || '',
      owner: initialData.owner
        ? [{ key: initialData.owner.id, fullName: initialData.owner.profile.fullName, email: initialData.owner.email }]
        : [],
      formalOwner: initialData.formalOwner
        ? [{ key: initialData.formalOwner.id, fullName: initialData.formalOwner.profile.fullName, email: initialData.formalOwner.email }]
        : [],
      info: {
        internalAssetIdentity: initialData.info.internalAssetIdentity || '',
        assetSubcategory: initialData.info.assetSubcategory || '',
        assetDescription: initialData.info.assetDescription || '',
        confidentiality: initialData.info.confidentiality || '',
        integrityChangeControl: initialData.info.integrityChangeControl || '',
        assetsImportance: initialData.info.assetsImportance || '',
        availability: initialData.info.availability || '',
        placementOfAsset: initialData.info.placementOfAsset || '',
        currentStatusOfAsset: initialData.info.currentStatusOfAsset || '',
        economicalValueOfAsset: initialData.info.economicalValueOfAsset || '',
        legalDemandsCheckbox: !!initialData.info.legalDemands,
        legalDemands: initialData.info.legalDemands || '',
        identifiableInformation: initialData.info.identifiableInformation || '',
        identifiableInformationCheckbox: !!initialData.info.identifiableInformation,
        sensitiveIndividualInformation: initialData.info.sensitiveIndividualInformation || '',
        sensitiveIndividualInformationCheckbox: !!initialData.info.sensitiveIndividualInformation,
      },
    },
  });

  const styles = useStyles();
  const { information_label: formMessages } = assets_messages;
  const sectionTitles = useMemo(
    () => ({
      section1: t(formMessages.section_title_1),
      section2: t(formMessages.section_title_2),
      section3: t(formMessages.section_title_3),
      section4: t(formMessages.section_title_4),
    }),
    [i18n.language, t],
  );

  const onDocumentDownload = (url: string, documentName: string) => async (event: React.MouseEvent) => {
    event.preventDefault();
    await downloadFromUrl(url, documentName);
  };

  return (
    <>
      <DetailsCardHeader
        onClose={onClose}
        subtitle={`${t(formMessages.friendly_id_label)}: ${initialData.key}`}
        title={t(initialData.name)}
      />
      <CenteredGrid className={styles.paddingSection} gridGap={1} width='md'>
        <Typography>{safeT(t, initialData.category.nameTranslationKey, initialData.category.name)}</Typography>
        <form className={styles.form} id={FORM_ID} onSubmit={formik.handleSubmit}>
          {/* @ts-ignore */}
          <CenteredGrid gridGap={2.5} width='md'>
            {sectionTitles.section1 && <Typography variant='h4'>{sectionTitles.section1}</Typography>}
            {isAssetManager && (
              <>
                <InputInfoWrapper description={t(formMessages.name_description)}>
                  <FormTextInput formik={formik} id='name' label={t(formMessages.name)} size='small' />
                </InputInfoWrapper>
                <InputInfoWrapper description={t(formMessages.category_description)}>
                  <FormSelect
                    formik={formik}
                    id='categoryId'
                    label={t(formMessages.category)}
                    options={categoriesOptions || []}
                    size='small'
                  />
                </InputInfoWrapper>
                <Divider />
              </>
            )}
            {sectionTitles.section2 && <Typography variant='h4'>{sectionTitles.section2}</Typography>}
            <InputInfoWrapper description={t(formMessages.internal_asset_identity_description)}>
              <FormTextInput formik={formik} id='info.internalAssetIdentity' label={t(formMessages.internal_asset_identity)} size='small' />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.asset_sub_category_description)}>
              <FormTextInput formik={formik} id='info.assetSubcategory' label={t(formMessages.asset_sub_category)} size='small' />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.asset_description_description)}>
              <FormTextInput
                formik={formik}
                id='info.assetDescription'
                label={t(formMessages.asset_description)}
                multiline
                rows={3}
                size='small'
              />
            </InputInfoWrapper>
            <Divider />
            {sectionTitles.section3 && <Typography variant='h4'>{sectionTitles.section3}</Typography>}
            <InputInfoWrapper description={t(formMessages.confidentiality_description)}>
              <FormSelect
                formik={formik}
                id='info.confidentiality'
                label={t(formMessages.confidentiality)}
                options={assetsInformationChoices?.confidentiality || []}
                size='small'
              />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.integrity_change_control_description)}>
              <FormSelect
                formik={formik}
                id='info.integrityChangeControl'
                label={t(formMessages.integrity_change_control)}
                options={assetsInformationChoices?.integrityChangeControl || []}
                size='small'
              />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.availability_description)}>
              <FormSelect
                formik={formik}
                id='info.availability'
                label={t(formMessages.availability)}
                options={assetsInformationChoices?.availability || []}
                size='small'
              />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.assets_importance_description)}>
              <FormSelect
                formik={formik}
                id='info.assetsImportance'
                label={t(formMessages.assets_importance)}
                options={assetsInformationChoices?.assetsImportance || []}
                size='small'
              />
            </InputInfoWrapper>
            <Divider />
            {sectionTitles.section4 && <Typography variant='h4'>{sectionTitles.section4}</Typography>}
            <InputInfoWrapper description={t(formMessages.placement_of_asset_description)}>
              <FormTextInput formik={formik} id='info.placementOfAsset' label={t(formMessages.placement_of_asset)} size='small' />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.current_status_of_asset_description)}>
              <FormTextInput formik={formik} id='info.currentStatusOfAsset' label={t(formMessages.current_status_of_asset)} size='small' />
            </InputInfoWrapper>
            <InputInfoWrapper description={t(formMessages.economical_value_of_asset_description)}>
              <FormTextInput
                formik={formik}
                id='info.economicalValueOfAsset'
                label={t(formMessages.economical_value_of_asset)}
                size='small'
              />
            </InputInfoWrapper>
            <Divider />
            <Typography variant='h4'>{t(formMessages.legal_title)}</Typography>
            <FormCheckboxTile
              description={t(formMessages.legal_demands_checkbox_description)}
              expandable
              formik={formik}
              id='info.legalDemandsCheckbox'
              label={t(formMessages.legal_demands_checkbox)}
              required={false}
            />
            <FormTextInput
              disabled={!formik.values.info.legalDemandsCheckbox}
              formik={formik}
              id='info.legalDemands'
              label={t(formMessages.legal_demands_input_label)}
              multiline
              rows={3}
              size='small'
            />
            <Divider />
            <Typography variant='h4'>{t(formMessages.gdpr_title)}</Typography>
            <FormCheckboxTile
              description={t(formMessages.identifiable_information_checkbox_description)}
              expandable
              formik={formik}
              id='info.identifiableInformationCheckbox'
              label={t(formMessages.identifiable_information_checkbox)}
              required={false}
            />
            <FormTextInput
              disabled={!formik.values.info.identifiableInformationCheckbox}
              formik={formik}
              id='info.identifiableInformation'
              label={t(formMessages.identifiable_information_input_label)}
              multiline
              rows={3}
              size='small'
            />
            <FormCheckboxTile
              description={t(formMessages.sensitive_individual_checkbox_description)}
              expandable
              formik={formik}
              id='info.sensitiveIndividualInformationCheckbox'
              label={t(formMessages.sensitive_individual_checkbox)}
              required={false}
            />
            <FormTextInput
              disabled={!formik.values.info.sensitiveIndividualInformationCheckbox}
              formik={formik}
              id='info.sensitiveIndividualInformation'
              label={t(formMessages.sensitive_individual_input_label)}
              multiline
              rows={3}
              size='small'
            />
            <Divider />
            <Typography variant='h4'>{t(formMessages.contacts_title)}</Typography>
            <div className={styles.contactsSection}>
              {!!initialData.contacts.length &&
                initialData.contacts.map(contact => {
                  const legacyContact = contact as typeof contact & {
                    contactCategory?: string;
                    contactName?: string;
                  };
                  const contactCategory = contact.category || legacyContact.contactCategory || '';
                  const contactName = contact.name || legacyContact.contactName || '';

                  return (
                    <div key={contact.id}>
                      <div className={styles.contactRow}>
                        <div className={styles.contactCell}>
                          <Typography>
                            {contactName} ({contactCategory})
                          </Typography>
                          {contact.comment && <Typography variant='caption'>{contact.comment}</Typography>}
                        </div>
                        <div className={styles.contactCell}>
                          <Typography align='right'>{`${t(general_messages.phone)}: ${contact.phoneNumber}`}</Typography>
                          <Typography align='right'>{`${t(general_messages.email)}: ${contact.email}`}</Typography>
                        </div>
                        <div className={styles.contactButtons}>
                          <IconButton onClick={openContactDialog(contact.id)} size='small'>
                            <PenIcon />
                          </IconButton>
                          <IconButton onClick={onRemoveContact(contact.id)} size='small'>
                            <TrashIcon />
                          </IconButton>
                        </div>
                      </div>
                      <Divider />
                    </div>
                  );
                })}
              <div className={styles.buttonWrapper}>
                {/* @ts-ignore */}
                <ColoredButton customColor='secondary' onClick={openContactDialog(null)}>
                  {t(assets_messages.details_page.add_contact_button)}
                </ColoredButton>
              </div>
            </div>
            <Divider />
            <Typography variant='h4'>{t(formMessages.files_title)}</Typography>
            <Typography variant='body2'>{t(formMessages.files_description)}</Typography>
            <div className={styles.filesSection}>
              {!!initialData.documents.length &&
                initialData.documents.map(document => {
                  const { id: documentId, label, createdAt, attachment, updatedAt } = document;
                  return (
                    <div key={documentId}>
                      <div className={styles.documentRow}>
                        <div className={styles.documentInfo}>
                          <Typography>{label}</Typography>
                          <Typography className={styles.documentName} variant='body2'>
                            {stringShortener(attachment.name, 40, true)}
                          </Typography>
                        </div>
                        <IconButton onClick={onDocumentDownload(attachment.url, attachment.name)} size='small'>
                          <DownloadIcon />
                        </IconButton>
                        <IconButton onClick={() => onDocumentRemove(document)} size='small'>
                          <TrashIcon />
                        </IconButton>
                        <Typography className={styles.details} variant='caption'>
                          {`${t(general_messages.created_at)}: ${format(createdAt, DATE_FORMAT.defaultDate)}`}
                          {', '}
                          {`${t(general_messages.updated_at)}: ${format(updatedAt, DATE_FORMAT.defaultDate)}`}
                        </Typography>
                      </div>
                      <Divider />
                    </div>
                  );
                })}
              <div className={styles.buttonWrapper}>
                {/* @ts-ignore */}
                <ColoredButton customColor='secondary' onClick={openUploadDialog}>
                  {t(assets_messages.details_page.upload_file_button)}
                </ColoredButton>
              </div>
            </div>
            <Divider />
            {isAssetManager ? (
              <>
                <FormAutocomplete
                  apiCallParamsGetter={coworkerParamsGetter}
                  customizeLabel={coworkerResultComponent}
                  formik={formik}
                  helperText={t(general_messages.user_autocomplete_helper)}
                  id='owner'
                  label={t(formMessages.owner)}
                  multiple={false}
                  optionsParser={coworkerOptionsParser}
                />
                <FormAutocomplete
                  apiCallParamsGetter={coworkerParamsGetter}
                  customizeLabel={coworkerResultComponent}
                  formik={formik}
                  helperText={t(general_messages.user_autocomplete_helper)}
                  id='formalOwner'
                  label={t(formMessages.formal_owner)}
                  multiple={false}
                  optionsParser={coworkerOptionsParser}
                />
              </>
            ) : (
              <>
                <Typography variant='h4'>{t(formMessages.owner)}</Typography>
                <div className={styles.ownerTile}>
                  <UserAvatar size='m' userId={`${initialData.owner.id}`} />
                  <div className={styles.ownerName}>
                    <Typography>{initialData.owner.profile.fullName}</Typography>
                    <Typography>{initialData.owner.email}</Typography>
                  </div>
                </div>
                <Typography variant='h4'>{t(formMessages.formal_owner)}</Typography>
                {initialData.formalOwner ? (
                  <div className={styles.ownerTile}>
                    <UserAvatar size='m' userId={`${initialData.formalOwner.id}`} />
                    <div className={styles.ownerName}>
                      <Typography>{initialData.formalOwner.profile.fullName}</Typography>
                      <Typography>{initialData.formalOwner.email}</Typography>
                    </div>
                  </div>
                ) : (
                  <Typography>{t(general_messages.not_applicable)}</Typography>
                )}
              </>
            )}
            <Divider />
            <Typography variant='h4'>{t(assets_messages.relations.title)}</Typography>
            <AssetRelationsTable assetId={id} assetName={initialData.name} assetOwnerId={initialData.owner.id} />
          </CenteredGrid>
        </form>
        <ButtonsGrid>
          {/* @ts-ignore */}
          <ColoredButton customColor='none' onClick={onClose} variant='outlined'>
            {t(general_messages.cancel)}
          </ColoredButton>
          {/* @ts-ignore */}
          <ColoredButton customColor='secondary' form={FORM_ID} type='submit' variant='outlined'>
            {t(general_messages.save)}
          </ColoredButton>
        </ButtonsGrid>
      </CenteredGrid>
      {isUploadDialogOpen && (
        <UploadFileDialog
          acceptedTypes={CUSTOMER_UPLOAD_ACCEPTED_TYPES}
          onClose={closeUploadDialog}
          onSubmit={onFileDialogSubmit}
          open={isUploadDialogOpen}
          validateFile={validateCustomerUploadFile}
          withLabelField
        />
      )}
      {contactDialog.isOpen && (
        <AssetContactDialog assetId={id} contactId={contactDialog.contactId} onClose={closeContactDialog} open={contactDialog.isOpen} />
      )}
    </>
  );
};

export default AssetEditForm;
